import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PhoneCall } from "lucide-react";

const EMPTY_RESERVATION = {
  customer_name: "", customer_phone: "", customer_email: "",
  date: new Date().toISOString().split("T")[0], time: "13:00",
  guests: 2, seating_preference: "sin_preferencia", notes: "", admin_notes: "", status: "confirmed",
  policy_accepted: true, policy_channel: "admin",
};

export default function CreateReservationDialog({ open, onClose, authAxios, onRefresh, currentUser }) {
  const [newReservation, setNewReservation] = useState({ ...EMPTY_RESERVATION });
  const [creating, setCreating] = useState(false);

  const createReservation = async () => {
    if (!newReservation.customer_name || !newReservation.customer_phone || !newReservation.date || !newReservation.time) {
      toast.error("Nome, telefone, data e hora são obrigatórios");
      return;
    }
    setCreating(true);
    try {
      // Add audit trail for admin-created reservations
      const payload = {
        ...newReservation,
        policy_created_by_user_id: currentUser?.id,
        policy_created_by_role: currentUser?.role,
      };
      await authAxios.post("/reservations", payload);
      toast.success("Reserva criada");
      setNewReservation({ ...EMPTY_RESERVATION });
      onClose();
      onRefresh();
    } catch { toast.error("Erro ao criar reserva"); }
    setCreating(false);
  };

  const handleClose = () => {
    setNewReservation({ ...EMPTY_RESERVATION });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] max-w-md max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PhoneCall className="w-5 h-5 text-emerald-600" />Adicionar Reserva Manual
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Nome *</Label>
              <Input value={newReservation.customer_name} onChange={e => setNewReservation(p => ({ ...p, customer_name: e.target.value }))} placeholder="Nome do cliente" data-testid="new-res-name" />
            </div>
            <div>
              <Label className="text-xs">Telefone *</Label>
              <Input value={newReservation.customer_phone} onChange={e => setNewReservation(p => ({ ...p, customer_phone: e.target.value }))} placeholder="+351 912 345 678" data-testid="new-res-phone" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Email</Label>
            <Input value={newReservation.customer_email} onChange={e => setNewReservation(p => ({ ...p, customer_email: e.target.value }))} placeholder="email@exemplo.com" data-testid="new-res-email" />
          </div>
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            <div>
              <Label className="text-xs">Data *</Label>
              <Input type="date" value={newReservation.date} onChange={e => setNewReservation(p => ({ ...p, date: e.target.value }))} data-testid="new-res-date" />
            </div>
            <div>
              <Label className="text-xs">Hora *</Label>
              <Input type="time" value={newReservation.time} onChange={e => setNewReservation(p => ({ ...p, time: e.target.value }))} data-testid="new-res-time" />
            </div>
            <div>
              <Label className="text-xs">Pessoas</Label>
              <Input type="number" min="1" value={newReservation.guests} onChange={e => setNewReservation(p => ({ ...p, guests: Number(e.target.value) }))} data-testid="new-res-guests" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Localização</Label>
            <Select value={newReservation.seating_preference} onValueChange={v => setNewReservation(p => ({ ...p, seating_preference: v }))}>
              <SelectTrigger data-testid="new-res-seating"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sin_preferencia">Sem preferência</SelectItem>
                <SelectItem value="interior">Interior</SelectItem>
                <SelectItem value="exterior">Exterior/Esplanada</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Estado</Label>
            <Select value={newReservation.status} onValueChange={v => setNewReservation(p => ({ ...p, status: v }))}>
              <SelectTrigger data-testid="new-res-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="confirmed">Confirmada</SelectItem>
                <SelectItem value="pending">Pendente</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Notas do cliente</Label>
            <Textarea value={newReservation.notes} onChange={e => setNewReservation(p => ({ ...p, notes: e.target.value }))} rows={2} placeholder="Alergias, celebração..." data-testid="new-res-notes" />
          </div>
          <div>
            <Label className="text-xs">Notas internas (admin)</Label>
            <Textarea value={newReservation.admin_notes} onChange={e => setNewReservation(p => ({ ...p, admin_notes: e.target.value }))} rows={2} placeholder="Notas visíveis só para a equipa" data-testid="new-res-admin-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Cancelar</Button>
          <Button onClick={createReservation} disabled={creating} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-new-reservation-btn">
            {creating ? "A guardar..." : "Criar Reserva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
